import React, { useState, useEffect, useMemo } from "react"
import { Container, Form, FormGroup, Input, Alert, Button } from "reactstrap"
import { Trans, useLingui } from "@lingui/react"
import PropTypes from "prop-types"
import { toast } from "react-toastify"
import { connect } from "react-redux"
import { postCreateMeeting } from "../../store/actions/meetings/createMeeting"
import { selectCreatedVslaInfo } from "../../store/reducers/getVslas"
import { setCurrentPage } from "../../store/reducers/navigation"
import { getUserGeoData } from "../../view/shared/helpers"
import { postGeoLocationLat, postGeoLocationLong } from "../../store/actions/geoData"
import StellarLoader from "../layout/StellarLoader"

const styles = {
  header: {
    color: "#E36F1E",
    fontFamily: "quicksand",
    fontWeight: "bolder",
    fontSize: "1.8em",
    textAlign: "center",
    paddingBottom: "0.5em",
  },
  subheader: {
    padding: ".3rem",
    textAlign: "center",
    fontFamily: "Fira Sans",
    fontSize: "20px",
    color: "#696969",
    fontStyle: "italic",
    fontWeight: "bold",
  },
  list: {
    fontFamily: "Fira Sans",
    fontSize: "20px",
    padding: "3px",
  },
  loading: {
    fontFamily: "Fira Sans",
    fontSize: "20px",
  },
};

const SuccessMessage = () => (
  <div style={styles.loading}>
    <Trans id="meeting.created.successfully">Meeting created successfully</Trans>
  </div>
);

const ErrorMessage = () => (
  <div style={styles.loading}>
    <Trans id="error.creating.meeting">Error creating meeting</Trans>
  </div>
);

const notify = (msg) => toast.info(msg);

// Helper function for today's date
const getTodaysDate = () => new Date().toISOString().slice(0, 10);

const CreateMeeting = (props) => {
  const {
    vslaSummary,
    loadedvslaMembers,
    geoLat,
    geoLong,
    sendGeoDataLat,
    sendGeoDataLong,
    setPage,
    sendMeetingData,
  } = props;

  const { i18n } = useLingui();

  const [date, setDate] = useState(getTodaysDate());
  const [isLoading, setIsLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [errors, setErrors] = useState("");

  const vslaInfo = useMemo(() => {
    if (!vslaSummary?.[0]) return {};
    const { vsla, group } = vslaSummary[0];
    const activeMembers = loadedvslaMembers;
    return {
      vslaId: vsla?.id,
      groupId: group?.id,
      groupDescription: vsla?.description,
      country: group?.country,
      county: group?.county,
      village: group?.village,
      attendees: activeMembers,
    };
  }, [vslaSummary, loadedvslaMembers]);

  useEffect(() => {
    const success = (pos) => {
      sendGeoDataLat(pos.coords.latitude);
      sendGeoDataLong(pos.coords.longitude);
    };
    const error = (err) => {
      console.warn(`Error ${err.code}: ${err.message}`);
      setErrors("Unable to retrieve your location.");
    };
    getUserGeoData(success, error);
  }, [sendGeoDataLat, sendGeoDataLong]);

  const createMeeting = async (meetingDate) => {
    const { groupId, country, county, village, attendees, groupDescription } = vslaInfo;

    const finalDate = new Date(meetingDate);
    const lang = i18n.locale || "en";

    // Simplified notification message logic
    const notifMessages = {
      en: `The secretary has started a meeting for ${groupDescription} group on VSLA Online`,
      fr: `FRA - The secretary has started a meeting for ${groupDescription} group on VSLA Online`,
      sw: `SWA - The secretary has started a meeting for ${groupDescription} group on VSLA Online`,
      es: `La secretaria ha iniciado una reunión para el grupo ${groupDescription} en VSLA Online`,
      lb: `The officer has started a session for ${groupDescription} community on VSLA.online`,
      pt: `O/A secretário/a iniciou uma reunião para o grupo ${groupDescription} no VSLA Online`,
      ar: groupDescription,
    };

    const data = {
      date: finalDate,
      status: "ongoing",
      longi: geoLong,
      lati: geoLat,
      county,
      village,
      country,
      attendees: attendees.filter(member => member?.pubkey).map(({ pubkey }) => pubkey),
      msg: notifMessages[lang] || notifMessages.en,
    };

    if (data.attendees.length <= 1) {
      alert("Add members before starting a meeting")
      return
    }
    setIsLoading(true);
    setButtonDisabled(true);

    try {
      await sendMeetingData(data);
      notify(<SuccessMessage />);
      setPage("group-meetingsMenu");
    } catch (err) {
      console.error("Error creating meeting:", err);
      notify(<ErrorMessage />);
      setErrors("There was an issue creating the meeting. Please try again.");
    } finally {
      setIsLoading(false);
      setButtonDisabled(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    await createMeeting(date);
  };

  const { attendees = [], groupDescription } = vslaInfo;
  const hasMembers = attendees.length > 0;
  const isFormDisabled = !hasMembers || buttonDisabled;

  if (isLoading) {
    return <StellarLoader />;
  }

  return (
    <div>
      <div style={{ backgroundColor: "white", padding: ".5rem", textAlign: "center", margin: "34px 0 0 0" }}>
        <h4 style={styles.header}>
          <Trans id="create.meeting">Create Meeting</Trans>
        </h4>
      </div>
      <Form onSubmit={onSubmit}>
        <Container>
          <FormGroup>
            <Input
              type="date"
              name="date"
              placeholder={i18n._("Meeting Date")}
              value={date}
              style={styles.loading}
              onChange={(e) => setDate(e.target.value)}
              max={getTodaysDate()}
            />
          </FormGroup>

          <div style={styles.subheader}>
            {groupDescription} <Trans id="meeting.members">Meeting Members</Trans>
          </div>
          <hr style={{ borderColor: "#E36F1E" }} />
          <FormGroup>
            <Container>
              {errors && <div className="alert alert-danger">{errors}</div>}
              <ul>
                {attendees.map((activeMember) => (
                  <li key={activeMember.id} style={styles.list} className="textStyle">
                    {activeMember.full_name}
                  </li>
                ))}
              </ul>
            </Container>
          </FormGroup>
        </Container>
        <Container>
          {!hasMembers && (
            <Alert color="warning">
              <span style={{ fontFamily: "Fira Sans", fontSize: "16px" }}>
                <Trans id="no.members.in.group">
                  There are no members in this group, please add members to create a meeting
                </Trans>
              </span>
            </Alert>
          )}
          <FormGroup>
            <Button
              type="submit"
              color="primary"
              size="sm"
              block
              name="submit"
              disabled={isFormDisabled}
            >
              {buttonDisabled ? i18n._("Submitting...") : i18n._("Submit")}
            </Button>
          </FormGroup>
        </Container>
      </Form>
    </div>
  );
};

CreateMeeting.propTypes = {
  geoLong: PropTypes.number,
  geoLat: PropTypes.number,
  vslaSummary: PropTypes.array,
  loadedvslaMembers: PropTypes.array,
  sendGeoDataLat: PropTypes.func,
  sendGeoDataLong: PropTypes.func,
  isConnected: PropTypes.bool,
  setPage: PropTypes.func,
  user: PropTypes.object,
  sendMeetingData: PropTypes.func,
};

const mapStateToProps = (state) => ({
  user: state.user,
  loadedvslaMembers: state.loadedvslaMembers,
  isConnected: state.isConnected,
  geoLat: state.geoDataReturnedLat,
  geoLong: state.geoDataReturnedLong,
  vslaSummary: selectCreatedVslaInfo(state),
});

const mapDispatchToProps = (dispatch) => ({
  sendGeoDataLat: (lat) => dispatch(postGeoLocationLat(lat)),
  sendGeoDataLong: (long) => dispatch(postGeoLocationLong(long)),
  setPage: (page) => dispatch(setCurrentPage(page)),
  sendMeetingData: (data) => dispatch(postCreateMeeting(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateMeeting);